import { z } from "zod";

import type { Review, ReviewOffer, ReviewReceipt, SentReview } from "./types";

/** The service sends `null` where TypeScript has `undefined`. */
const maybe = <T extends z.ZodTypeAny>(schema: T) =>
  schema.nullish().transform((value): z.output<T> | undefined => value ?? undefined);

const decisionKind = z.enum(["approve", "approve_with_comments", "request_changes"]);

const reviewSchema = z.object({
  id: z.string(),
  linkId: z.string(),
  drawing: z.string(),
  drawingName: z.string(),
  drawingVersion: z.number(),
  versionHash: maybe(z.string()),
  title: z.string(),
  message: z.string(),
  recipientName: z.string(),
  recipientContact: maybe(z.string()),
  dueOn: maybe(z.string()),
  createdBy: z.string(),
  createdAt: z.string(),
  expiresAt: maybe(z.string()),
  status: z.enum([
    "pending",
    "approved",
    "approved_with_comments",
    "changes_requested",
    "expired",
    "revoked",
  ]),
  decision: maybe(
    z.object({
      decision: decisionKind,
      note: z.string(),
      approverName: z.string(),
      decidedAt: z.string(),
      signOffSeq: maybe(z.number()),
      signOffHash: maybe(z.string()),
    }),
  ),
});

export const parseReviews = (value: unknown): Review[] => z.array(reviewSchema).parse(value);
export const parseSent = (value: unknown): SentReview =>
  z.object({ review: reviewSchema, url: z.string() }).parse(value);

export const offerSchema = z.object({
  id: z.string(),
  title: z.string(),
  message: z.string(),
  drawingVersion: z.number(),
  dueOn: maybe(z.string()),
  decision: maybe(
    z.object({
      decision: decisionKind,
      note: z.string(),
      decidedAt: z.string(),
      receipt: maybe(z.string()),
    }),
  ),
});

export const parseOffer = (value: unknown): ReviewOffer => offerSchema.parse(value);

export const parseReceipt = (value: unknown): ReviewReceipt =>
  z.object({ decision: decisionKind, decidedAt: z.string(), receipt: z.string() }).parse(value);
